<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- Origin egress column 0.1.154; 2026-10-28 16:23:38 CST; parent 6eb2e62e13e2f6183fa985be42c2cbb1f19ed4e2. -->
<script setup lang="ts">
import { ElMessage } from 'element-plus'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { consoleApi, CONSOLE_SESSION_CHANGED_EVENT, tokenStorage } from '../api/client'

const props = defineProps<{ domainId: number; refreshKey: number; copyText: (value: string) => Promise<void> }>()
const ips = ref<string[]>([])
const status = ref<'loading' | 'ready' | 'error' | 'guest'>('loading')
const feedback = ref('')
const copying = ref(false)
const copyKey = ref('')
const copiedKey = ref('')
const failedKey = ref('')
function copyLabel(key: string) {
  if (copying.value && copyKey.value === key) return '复制中…'
  if (copiedKey.value === key) return '已复制'
  if (failedKey.value === key) return '重试复制'
  return '复制'
}
let sequence = 0
let active = true
function session() { return [tokenStorage.getAccessToken(), tokenStorage.getCustomerId(), tokenStorage.getMustChangePassword()] as const }
function sameSession(previous: ReturnType<typeof session>) { return previous.every((value, index) => value === session()[index]) }
const first = computed(() => ips.value[0])
async function load() {
  const request = ++sequence, owner = session()
  ips.value = []; feedback.value = ''; copiedKey.value = ''; failedKey.value = ''
  if (!owner[0] || owner[2]) { status.value = 'guest'; return }
  status.value = 'loading'
  try {
    const response = await consoleApi.portal.originWhitelist(props.domainId) as { addresses?: { ipAddress?: string }[] }
    if (!active || request !== sequence || !sameSession(owner)) return
    if (!Array.isArray(response?.addresses)) throw new Error('Invalid origin whitelist response')
    // Only the authorized ORIGIN_EGRESS endpoint supplies these values; never infer from ingress/DNS.
    ips.value = [...new Set(response.addresses.map(item => item.ipAddress?.trim()).filter((ip): ip is string => Boolean(ip)))]
    status.value = 'ready'
  } catch {
    if (active && request === sequence && sameSession(owner)) status.value = 'error'
  }
}
async function copy(values: string[], all = false) {
  if (copying.value || !values.length) return
  const owner = session(), request = sequence
  copying.value = true; feedback.value = ''; copiedKey.value = ''; failedKey.value = ''; copyKey.value = all ? 'all' : values[0]
  try {
    await props.copyText(values.join('\n'))
    if (active && request === sequence && sameSession(owner)) {
      copiedKey.value = all ? 'all' : values[0]
      feedback.value = all ? `已复制全部 ${values.length} 个 IP` : `已复制 IP：${values[0]}`
      ElMessage.success({ message: feedback.value, duration: 2500, showClose: true })
    }
  } catch {
    if (active && request === sequence && sameSession(owner)) {
      failedKey.value = all ? 'all' : values[0]
      feedback.value = '复制失败，请重试或选中 IP 手动复制。'
      ElMessage.error({ message: feedback.value, duration: 4000, showClose: true })
    }
  } finally { copying.value = false }
}
watch(() => [props.domainId, props.refreshKey], load, { immediate: true })
onMounted(() => window.addEventListener(CONSOLE_SESSION_CHANGED_EVENT, load))
onBeforeUnmount(() => { active = false; sequence++; window.removeEventListener(CONSOLE_SESSION_CHANGED_EVENT, load) })
</script>

<template>
  <div class="origin-ip-cell" aria-live="polite">
    <span v-if="status === 'loading'" class="origin-ip-status">正在读取回源 IP…</span>
    <span v-else-if="status === 'guest'" class="origin-ip-status">登录后查看回源 IP</span>
    <template v-else-if="status === 'error'">
      <span class="origin-ip-error">回源 IP 读取失败</span>
      <el-button data-test="retry" link type="primary" size="small" @click.stop="load">重试</el-button>
    </template>
    <span v-else-if="!first" class="origin-ip-status">暂无已确认的回源 IP，请联系管理员核实。</span>
    <template v-else>
      <div class="origin-ip-summary">
        <button class="origin-ip-chip" :disabled="copying" :aria-label="`复制回源 IP ${first}`" @click.stop="copy([first])">
          <span>{{ first }}</span><small v-if="copyLabel(first) !== '复制'">{{ copyLabel(first) }}</small>
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="7" y="3" width="10" height="12" rx="2"/><path d="M12 17H5a2 2 0 0 1-2-2V7"/></svg>
        </button>
        <el-popover placement="bottom-start" trigger="click" :width="320" :popper-style="{ maxWidth: 'calc(100vw - 32px)' }" :teleported="true">
          <template #reference>
            <button class="origin-ip-expand" :aria-label="`查看全部 ${ips.length} 个回源 IP`">
              <span v-if="ips.length > 1">+{{ ips.length - 1 }} 个</span><span>查看全部</span>
              <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg>
            </button>
          </template>
          <div class="origin-ip-popover">
            <strong>回源 IP · 共 {{ ips.length }} 个</strong>
            <p>请将当前与备用回源 IP 一并加入源站白名单。</p>
            <ul>
              <li v-for="(ip, index) in ips" :key="ip">
                <span class="origin-ip-version">{{ ip.includes(':') ? 'IPv6' : 'IPv4' }}</span>
                <span class="origin-ip-address">{{ ip }}</span>
                <el-button :data-test="`copy-ip-${index}`" :aria-label="`复制 ${ip}`" :disabled="copying" link type="primary" size="small" @click="copy([ip])">{{ copyLabel(ip) }}</el-button>
              </li>
            </ul>
            <el-button data-test="copy-all" class="origin-ip-copy-all" :disabled="copying" type="success" plain @click="copy(ips, true)">{{ copyLabel('all') === '复制' ? '复制全部 IP' : copyLabel('all') === '已复制' ? '已复制全部 IP' : copyLabel('all') }}</el-button>
            <p v-if="feedback" role="status">{{ feedback }}</p>
          </div>
        </el-popover>
      </div>
      <span class="origin-ip-status">用于源站防火墙放行</span>
    </template>
    <span v-if="feedback" class="origin-ip-feedback" role="status">{{ feedback }}</span>
  </div>
</template>

<style scoped>
.origin-ip-cell { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; padding: 4px 0; }
.origin-ip-summary { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.origin-ip-chip, .origin-ip-expand { display: inline-flex; align-items: center; gap: 8px; border: 1px solid #bddbc9; border-radius: 6px; padding: 6px 8px; background: #f4faf5; color: #165a3d; font: inherit; cursor: pointer; }
.origin-ip-chip span { overflow-wrap: anywhere; text-align: left; font-variant-numeric: tabular-nums; }
.origin-ip-chip svg { flex-shrink: 0; }
.origin-ip-expand { background: #f6f8fb; color: #34516b; border-color: #d2deea; font-size: 12px; }
.origin-ip-chip:hover, .origin-ip-expand:hover { border-color: #27825b; background: #eaf5ed; }
.origin-ip-chip:focus-visible, .origin-ip-expand:focus-visible { outline: 2px solid #27825b; outline-offset: 2px; }
.origin-ip-chip:disabled { opacity: .65; cursor: wait; }
.origin-ip-status { color: #536b60; font-size: 12px; line-height: 1.5; }
.origin-ip-error { color: #a3312a; font-size: 12px; }
.origin-ip-feedback { color: #165a3d; font-size: 12px; }
.origin-ip-popover { color: #203b30; }
.origin-ip-popover p { color: #536b60; font-size: 12px; line-height: 1.6; margin: 8px 0; }
.origin-ip-popover ul { list-style: none; padding: 0; margin: 12px 0; max-height: 240px; overflow-y: auto; }
.origin-ip-popover li { display: flex; align-items: center; gap: 8px; padding: 7px 0; }
.origin-ip-version { color: #536b60; font-size: 12px; flex-shrink: 0; }
.origin-ip-address { overflow-wrap: anywhere; flex: 1; font-variant-numeric: tabular-nums; }
.origin-ip-copy-all { width: 100%; }
</style>
